import { tick } from "svelte"
import { get } from "svelte/store"
import { produce, setAutoFreeze } from "immer"

setAutoFreeze(false)

export async function applyConfigurationToGanttStore(GanttStore, configuration) {
  // set the current style
  // load the style name setting from localStorage, and if it is a valid style, set it
  const savedStyle = configuration.styleObject;
  if (savedStyle && get(GanttStore.StyleObjects)?.find((s) => s.ObjectID == savedStyle.ObjectID)) {
    GanttStore.CurrentStyle.set(savedStyle)
  }

  // set display span
  if (configuration.displaySpanInSeconds) {
    GanttStore.DisplayedSpanInSeconds.set(configuration.displaySpanInSeconds);
  }
  // set startDayDisplayTime
  if (configuration.startDayDisplayTime) {
    GanttStore.StartDayDisplayTime.set(configuration.startDayDisplayTime);
  }
  // set endDayDisplayTime
  if (configuration.endDayDisplayTime) {
    GanttStore.EndDayDisplayTime.set(configuration.endDayDisplayTime);
  }
  // set the current displayed start date only when it is in the displayed time range
  if (configuration.currentDisplayedStartDate) {
    const saved = new Date(configuration.currentDisplayedStartDate)
    if (saved.getTime() >= get(GanttStore.ValidStartDate).getTime() && saved.getTime() <= get(GanttStore.ValidEndDate).getTime()) {
      GanttStore.CurrentDisplayedStartDate.set(saved)
    }
  }
  // set unfolded operations
  if (configuration.unfoldedOperations) {
    GanttStore.RowsUnfoldedForOrderGantt.set(new Set(configuration.unfoldedOperations));
  }
  // set extra columns
  if (configuration.extraColumns) {
    GanttStore.ExtraColumns.update(columns => {
      if (configuration.extraColumns.length !== columns.length) return columns
      return configuration.extraColumns
    })
  }
  if (configuration.filterExclusionLists) {
    GanttStore.Filters.update(filters => produce(filters, draft => {
      for (const filterName in configuration.filterExclusionLists) {
        if (!(filterName in draft)) continue
        const exclusionSet = new Set(configuration.filterExclusionLists[filterName])
        for (const item of draft[filterName].items) {
          if (exclusionSet.has(item.text)) item.excluded = true
        }
      }
    }))
  }
  // set dented view
  if (configuration.dentedView !== undefined) {
    GanttStore.DentedView.set(configuration.dentedView)
  }

  // set hide days off/weekends
  if (configuration.hideDaysOff !== undefined) {
    GanttStore.HideDaysOff.set(configuration.hideDaysOff)
  }
  if (configuration.hideWeekEnds !== undefined) {
    GanttStore.HideWeekEnds.set(configuration.hideWeekEnds)
  }

  // set nest count
  if (configuration.nestCount !== undefined) {
    const _style = get(GanttStore.CurrentStyle);

    if (configuration.nestCount[_style.ObjectID] !== undefined) {
      GanttStore.ConnectingLinesDepth.set(configuration.nestCount[_style.ObjectID]);
    }
  }


  if (configuration.shouldAskConfirmationBeforeReloadingForAutoUpdate !== undefined) {
    GanttStore.ShouldAskConfirmationBeforeReloadingForAutoUpdate.set(configuration.shouldAskConfirmationBeforeReloadingForAutoUpdate)
  }

  // we have to call tick(), because DisplayedRowCount and rowsDisplayedStart are reset by setting RowSearchPattern.
  await tick()

  // set rowCount
  if (configuration.rowCount) {
    GanttStore.DisplayedRowCount.set(configuration.rowCount);
  }
  await tick()
  // set rowsDisplayedStart only when it is in the rows range
  if (configuration.rowsDisplayedStart) {
    const saved = configuration.rowsDisplayedStart
    if (saved > 0 && saved < get(GanttStore.RowsCountTotal)) {
      GanttStore.RowsDisplayedStart.set(saved)
    }
  }
}

export async function subscribeGanttStoreWithConfiguration(GanttStore, saveFunction, getFunction) {
  GanttStore.RowsDisplayedStart.subscribe(val => {
    saveFunction({rowsDisplayedStart: val})
  })
  GanttStore.RowsUnfoldedForOrderGantt.subscribe(val => {
    saveFunction({unfoldedOperations: Array.from(val)})
  })
  GanttStore.CurrentStyle.subscribe(val => {
    if (Object.keys(val).length) {
      saveFunction({styleObject: val})

      // Odd workaround but it is the only not-too-dirty way to cache it as I need on style-change, please do not delete
      if (getFunction !== undefined) {
        const configuration = getFunction();
        if (configuration.nestCount !== undefined && configuration.nestCount[val.ObjectID] !== undefined)
          GanttStore.ConnectingLinesDepth.set(configuration.nestCount[val.ObjectID]);
        else
          GanttStore.ConnectingLinesDepth.set(get(GanttStore.CurrentStyleInstance)?.ConnectionLineNestCount ?? 50);
      }
    }
  })
  GanttStore.DisplayedRowCount.subscribe(val => {
    saveFunction({rowCount: val})
  })
  GanttStore.CurrentDisplayedStartDate.subscribe(val => {
    saveFunction({currentDisplayedStartDate: val.toISOString()})
  })
  GanttStore.DisplayedSpanInSeconds.subscribe(val => {
    saveFunction({displaySpanInSeconds: Math.floor(val)})
  })
  GanttStore.StartDayDisplayTime.subscribe(val => {
    saveFunction({startDayDisplayTime: val})
  })
  GanttStore.EndDayDisplayTime.subscribe(val => {
    saveFunction({endDayDisplayTime: val})
  })
  GanttStore.ExtraColumns.subscribe(val => {
    saveFunction({extraColumns: val})
  })
  GanttStore.DentedView.subscribe(val => {
    saveFunction({dentedView: val})
  })
  GanttStore.Filters.subscribe(val => {
    const filterExclusionLists = {}
    for (const filterName in val) {
      filterExclusionLists[filterName] = val[filterName].items
        .filter(item => item.excluded)
        .map(item => item.text)
    }
    saveFunction({filterExclusionLists})
  })
  GanttStore.HideDaysOff.subscribe(val => {
    saveFunction({hideDaysOff: val})
  })
  GanttStore.HideWeekEnds.subscribe(val => {
    saveFunction({hideWeekEnds: val})
  })
  GanttStore.ShouldAskConfirmationBeforeReloadingForAutoUpdate.subscribe(val => {
    saveFunction({shouldAskConfirmationBeforeReloadingForAutoUpdate: val})
  })
  GanttStore.ConnectingLinesDepth.subscribe(val => {
    const _key = get(GanttStore.CurrentStyle).ObjectID;
    saveFunction({nestCount: {
      [_key]: val
    }})
  })
}
